import apiService from './ApiService';

const ThreadsService = {
  /**
   * Obtiene los mensajes de threads de un asistente con paginación
   * @param {string} idAsistente - ID del asistente
   * @param {string} filtroRol - Rol a filtrar ('user' para mensajes de alumnos)
   * @param {number} pagina - Página actual (default: 1)
   * @param {number} tamañoPagina - Threads por página (default: 50, max: 200)
   * @param {number} limiteMensajesPorThread - Mensajes por thread (default: 100, max: 500)
   * @returns {Promise} Promesa que resuelve con la respuesta de la API
   */
  async getThreadsMessages(
    idAsistente, 
    filtroRol = 'user', 
    pagina = 1, 
    tamañoPagina = 50, 
    limiteMensajesPorThread = 100
  ) {
    try {
      
      const requestBody = {
        IdAsistente: idAsistente,
        FiltroRol: filtroRol,
        Pagina: pagina,
        TamañoPagina: Math.min(tamañoPagina, 200), // Máximo 200
        LimiteMensajesPorThread: Math.min(limiteMensajesPorThread, 500) // Máximo 500
      };
      
      
      const response = await apiService.post('/Asistente/threads-messages', requestBody);
      
      
      // Validar estructura de respuesta
      if (!response.ok) {
        throw new Error(response.error || 'Error al obtener los mensajes');
      }
      
      // Procesar la nueva estructura de respuesta con threads
      if (response.threads && Array.isArray(response.threads)) {
        // Aplanar los mensajes de todos los threads para la tabla
        const mensajesAplanados = [];
        
        response.threads.forEach(thread => {
          if (thread.mensajes && Array.isArray(thread.mensajes)) {
            thread.mensajes.forEach(mensaje => {
              mensajesAplanados.push({
                ...mensaje,
                threadId: thread.idThread,
                fechaCreacionThread: thread.fechaCreacion ? new Date(thread.fechaCreacion) : null,
                fechaEnviado: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
                fecha: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
                mensaje: mensaje.contenido || '',
                usuario: mensaje.rol === 'user' ? 'Estudiante' : 'Asistente',
                rol: mensaje.rol
              });
            });
          }
        });
        
        return {
          mensajes: mensajesAplanados,
          threads: response.threads,
          totalThreads: response.totalThreads || 0,
          totalMessages: response.totalMessages || 0,
          paginaActual: response.paginaActual || pagina,
          tamañoPagina: response.tamañoPagina || tamañoPagina,
          totalThreadsEnBD: response.totalThreadsEnBD || 0,
          totalPaginas: response.totalPaginas || 1,
          tienePaginaSiguiente: response.tienePaginaSiguiente || false,
          tienePaginaAnterior: response.tienePaginaAnterior || false,
          total: response.totalMessages || 0,
          ok: response.ok,
          statusCode: response.statusCode
        };
      }
      
      // Fallback para retrocompatibilidad
      return {
        mensajes: [],
        threads: [],
        totalThreads: 0,
        totalMessages: 0,
        paginaActual: pagina,
        tamañoPagina: tamañoPagina,
        totalPaginas: 1,
        total: 0,
        ok: response.ok,
        statusCode: response.statusCode
      };
      
    } catch (error) {
      console.error('❌ Error al obtener mensajes:', error);
      throw error;
    }
  },

  /**
   * Obtiene los mensajes de threads de un asistente desde la BD (sin OpenAI)
   * @param {string} idAsistente - ID del asistente
   * @param {string} filtroRol - Rol a filtrar ('user' para mensajes de alumnos)
   * @param {number} pagina - Página actual (default: 1)
   * @param {number} tamañoPagina - Threads por página (default: 50, max: 200)
   * @param {number} limiteMensajesPorThread - Mensajes por thread (default: 100, max: 500)
   * @returns {Promise} Promesa que resuelve con la respuesta de la API
   */
  async getThreadsMessagesFromDb(
    idAsistente,
    filtroRol = 'user',
    pagina = 1,
    tamañoPagina = 50,
    limiteMensajesPorThread = 100,
    filters = {}
  ) {
    try {
      const requestBody = {
        IdAsistente: idAsistente,
        FiltroRol: filtroRol || null,
        Pagina: pagina,
        TamañoPagina: Math.min(tamañoPagina, 200),
        LimiteMensajesPorThread: Math.min(limiteMensajesPorThread, 500),
        FechaDesde: filters.fechaDesde || null,
        FechaHasta: filters.fechaHasta || null
      };

      const response = await apiService.post('/Asistente/threads-messages-db', requestBody);

      if (!response.ok) {
        throw new Error(response.error || 'Error al obtener los mensajes');
      }

      if (response.threads && Array.isArray(response.threads)) {
        const mensajesAplanados = [];

        response.threads.forEach(thread => {
          if (thread.mensajes && Array.isArray(thread.mensajes)) {
            thread.mensajes.forEach(mensaje => {
              mensajesAplanados.push({
                ...mensaje,
                threadId: thread.idThread,
                fechaCreacionThread: thread.fechaCreacion ? new Date(thread.fechaCreacion) : null,
                fechaEnviado: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
                fecha: mensaje.fechaEnvio ? new Date(mensaje.fechaEnvio) : null,
                mensaje: mensaje.contenido || '',
                usuario: mensaje.rol === 'user' ? 'Estudiante' : 'Asistente',
                rol: mensaje.rol
              });
            });
          }
        });

        return {
          mensajes: mensajesAplanados,
          threads: response.threads,
          totalThreads: response.totalThreads || 0,
          totalMessages: response.totalMessages || 0,
          paginaActual: response.paginaActual || pagina,
          tamañoPagina: response.tamañoPagina || tamañoPagina,
          totalThreadsEnBD: response.totalThreadsEnBD || 0,
          totalPaginas: response.totalPaginas || 1,
          tienePaginaSiguiente: response.tienePaginaSiguiente || false,
          tienePaginaAnterior: response.tienePaginaAnterior || false,
          total: response.totalMessages || 0,
          ok: response.ok,
          statusCode: response.statusCode
        };
      }

      return {
        mensajes: [],
        threads: [],
        totalThreads: 0,
        totalMessages: 0,
        paginaActual: pagina,
        tamañoPagina: tamañoPagina,
        totalPaginas: 1,
        total: 0,
        ok: response.ok,
        statusCode: response.statusCode
      };
    } catch (error) {
      console.error('❌ Error al obtener mensajes desde BD:', error);
      throw error;
    }
  },

  /**
   * Obtiene la conversación completa de un thread específico
   * @param {string} idAsistente - ID del asistente (formato OpenAI)
   * @param {string} idThread - ID del thread
   * @param {number} limiteMensajes - Límite de mensajes (default: 100)
   * @returns {Promise} Promesa que resuelve con la conversación completa
   */
  async getThreadConversation(idAsistente, idThread, limiteMensajes = 100, options = {}) {
    try {
      const useOpenAI = options.useOpenAI !== false;
      const regionalId = options.regionalId || null;
      
      const requestBody = {
        idAsistente: idAsistente,
        idThread: idThread,
        limiteMensajes: limiteMensajes,
        usarOpenAI: useOpenAI,
        regionalId: regionalId
      };
      
      
      const response = await apiService.post('/Asistente/thread-conversation', requestBody);
      
      
      // Validar estructura de respuesta
      if (!response.ok) {
        throw new Error(response.error || 'Error al obtener la conversación');
      }
      
      return {
        idThread: response.idThread,
        idAsistente: response.idAsistente,
        fechaCreacionThread: response.fechaCreacionThread,
        mensajes: response.mensajes || [],
        totalMensajes: response.totalMensajes || 0,
        ok: response.ok,
        statusCode: response.statusCode
      };
      
    } catch (error) {
      console.error('❌ Error al obtener conversación:', error);
      throw error;
    }
  },

  async getGptClasicoMensajes(idAsistente, pagina = 1, tamañoPagina = 50, busqueda = '', filters = {}, filtroRol = '') {
    try {
      const params = new URLSearchParams({
        idAsistente,
        pagina: String(pagina),
        'tamañoPagina': String(Math.min(tamañoPagina, 200))
      });
      if (busqueda) {
        params.set('busqueda', busqueda);
      }
      if (filters.fechaDesde) {
        params.set('fechaDesde', filters.fechaDesde);
      }
      if (filters.fechaHasta) {
        params.set('fechaHasta', filters.fechaHasta);
      }
      if (filtroRol) {
        params.set('filtroRol', filtroRol);
      }

      const response = await apiService.request(`/Asistente/gpt-clasico-mensajes?${params.toString()}`, {
        method: 'GET'
      });

      const mensajes = (response.mensajes || []).map(m => ({
        ...m,
        createdAt: m.createdAt ? new Date(m.createdAt) : null,
        receivedAt: m.receivedAt ? new Date(m.receivedAt) : null
      }));

      return {
        ok: true,
        mensajes,
        totalMensajes: response.totalMensajes || 0,
        paginaActual: response.paginaActual || pagina,
        tamañoPagina: response.tamañoPagina || tamañoPagina,
        totalPaginas: response.totalPaginas || 0,
        tienePaginaSiguiente: response.tienePaginaSiguiente || false,
        tienePaginaAnterior: response.tienePaginaAnterior || false
      };
    } catch (error) {
      console.error('Error al obtener mensajes GPT clásico:', error);
      throw error;
    }
  },

  async getAnalisisDocenteMensajes(idAsistente, filters = {}, options = {}) {
    try {
      const response = await apiService.post('/Asistente/reportes/analisis-docente', {
        IdAsistente: idAsistente,
        FiltroRol: options.filtroRol || null,
        FechaDesde: filters.fechaDesde || null,
        FechaHasta: filters.fechaHasta || null
      });

      if (!response.ok) {
        throw new Error(response.error || response.message || 'No se pudo generar el análisis docente.');
      }

      return response;
    } catch (error) {
      console.error('Error al generar análisis docente:', error);
      throw error;
    }
  }
};

export default ThreadsService;
